import * as Sentry from "@sentry/vue";
import AlertRequestQuery from "~/entities/AlertRequestQuery";
import Box from "~/entities/Box";
import { Country } from "~/entities/Response/FormData";
import Trip from "~/entities/Trip";
import ValidationModule from "~/entities/ValidationModule";
import StoreHelper from "~/entities/StoreHelper";
import IsAddressFilled from "~/assets/utils/isAddressFilled";
import {
  isFedExOffice,
  isPoBox,
  isPostalService,
  isUpsStore,
} from "~/assets/utils/carrierAddressGuards";

export default class ValidationSearch extends ValidationModule {
  // alert clusters
  public countryAlerts = {
    selector: {
      from: "AlertInvalidFromCountry",
      to: "AlertInvalidCountry",
      intShip: "No3P3CAtThisTime"
    },
    needle: ["#CountryName#"],
  };

  public rateAlerts = {
    selector: {
      default: "AlertFedExFailed",
      canada: "AlertFedExCanadaNoResults",
      timeOut: "AlertFedExTimeout",
      noResults: "AlertFedExNoResults",
    },
    needle: [""],
  };

  public nearAlerts = {
    selector: {
      dim: "AlertNearSizeLimit",
      size: "AlertNearDimLimit",
      weight: "AlertNearWeightLimit",
    },
    needle: [""],
  };

  public overAlerts = {
    selector: {
      dim: "AlertOverSizeLimit",
      size: "AlertOverDimLimit",
      weight: "AlertOverWeightLimit",
    },
    needle: [""],
  };

  public valueAlerts = {
    selector: {
      zero: "CommercialNoValue", // select by market value < 1?
      // low: 'PersonalNoValue',//select by market value < minMarketValue?
    },
    needle: [""],
  };

  public contentAlerts = {
    selector: {
      USeBike: "AlertEbikeShipment",
      eBike: "AlertEBikeInternational",
      scicon: "SciconWarning"
    },
    needle: [""],
  };

  public protectionAlerts = {
    selector: {
      high: "MultiplePackageHighValue", // On search step, triggers if one package's protection is greater than $1000 but another package's protection is less than $1000.
      less: "PPLessThanMV", // select by market value > premium protection?
      max: "ProtectionMax", // select by custom protection value > 20000?
    },
    needle: [""],
  };

  public purposeAlerts = {
    selector: {},
    needle: [""],
  };

  public stageAlerts = {
    selector: {
      maxStage: "AlertMaxStageWarning",
    },
    needle: [""],
  };

  // BIK-2135: carrier-restricted destinations on Ship1. Copy is reused from the
  // existing Ship3 DB alerts — nothing hardcoded here.
  // `ups` (AlertShipToUPS) is the only non-blocking one: a UPS Store is a valid
  // UPS destination, it just needs a call ahead.
  public addressAlerts = {
    selector: {
      fedEx: "AlertShipToFedEx",
      ups: "AlertShipToUPS",
      upsFrom: "AlertShipFromUPS",
      poBox: "POAddressWarning",
      poFromBox: "POFromAddressWarning",
    },
    needle: [""],
  };

  public dimensionLimits = {
    in: {
      maxL: 108,
      maxW: 108,
      maxH: 108,
      maxWeight: 150,
      weightNoVerify: 70,
      weightVerify: 145,
      maxDimensional: 165,
      maxLPS: 130,
      nearLimitDelta: 5,
      cubicNearLimit: 12000,
      cubicMaxLimit: 17280,
      weightNearRateIncrease: 100,
      weightMaxRateIncrease: 110,
    },
    cm: {
      maxL: 274,
      maxW: 274,
      maxH: 274,
      maxWeight: 68,
      weightNoVerify: 31,
      weightVerify: 66,
      maxDimensional: 419,
      maxLPS: 330,
      nearLimitDelta: 12,
      cubicNearLimit: 196645,
      cubicMaxLimit: 283168,
      weightNearRateIncrease: 45,
      weightMaxRateIncrease: 50
      
    },
  };

  public minMarketValue = 1;
  public maxMarketValue = 20000;
  public maxProtectionValue = 20000;
  public tripsLimit = 6;

  public constructor() {
    super("Search");
    this.alertNames = [
      "AlertInvalidFromCountry",
      "AlertInvalidCountry",
      "AlertNearSizeLimit",
      "AlertOverSizeLimit",
      "AlertNearDimLimit",
      "AlertOverDimLimit",
      "AlertNearWeightLimit",
      "AlertOverWeightLimit",
      "MultiplePackageHighValue",
      "AlertEstimatedDimensions",
      "AlertFreightSize",
      "AlertPostalCodeLookup",
      "CommercialNoValue",
      "PersonalNoValue",
      "ProtectionTooHigh",
      "PPLessThanMV",
      "AlertEbikeShipment",
      "AlertEBikeInternational",
      "AlertFedExFailed",
      "AlertFedExCanadaNoResults",
      "AlertMaxStageWarning",
      "SciconWarning",
      "No3P3CAtThisTime",
      "AlertProtectionTooLow",
      // BIK-2135: carrier-restricted destination alerts (shared with Ship3)
      "AlertShipToFedEx",
      "AlertShipToUPS",
      "AlertShipFromUPS",
      "POAddressWarning",
      "POFromAddressWarning",
    ];
    this.alertTypes = {
      country: this.countryAlerts,
      rates: this.rateAlerts,
      near: {},// this.nearAlerts, //commented out for now since we are testing the alerts near search button instead
      over: this.overAlerts,
      value: this.valueAlerts,
      protection: this.protectionAlerts,
      content: this.contentAlerts,
      purpose: this.purposeAlerts,
      stages: this.stageAlerts,
      address: this.addressAlerts
    };
  }

  /**
   * BIK-1904: Check cubic volume (LxWxH) for AlertNearLargePackage
   * Alert when volume is >12000 but <17280 cubic inches (or cm equivalent)
   * For volumes >= 17280, rate increases are handled by the backend pricing logic
   */
  private checkCubicVolumeAlert(length: number, width: number, height: number, weight: number, unit: string): void {
    const cubicNearLimit = unit === "cm"
      ? this.dimensionLimits.cm.cubicNearLimit
      : this.dimensionLimits.in.cubicNearLimit;
    const cubicMaxLimit = unit === "cm"
      ? this.dimensionLimits.cm.cubicMaxLimit
      : this.dimensionLimits.in.cubicMaxLimit;
    const weightNearRateIncrease = unit === "cm"
        ? this.dimensionLimits.cm.weightNearRateIncrease
        : this.dimensionLimits.in.weightNearRateIncrease;
    const weightMaxRateIncrease = unit === "cm"
        ? this.dimensionLimits.cm.weightMaxRateIncrease
        : this.dimensionLimits.in.weightMaxRateIncrease;

    const cubicVolume = length * width * height;
    if (cubicVolume > cubicNearLimit && cubicVolume < cubicMaxLimit || weight > weightNearRateIncrease && weight <= weightMaxRateIncrease ) {
      // BIK-1995: top banner removed — now shown inline via GuidanceHelper
      // this.requestAlert({ name: "nearLarge", type: "near" });
    }
  }

  public async Validate(scope: string = "") {
    // for later Validate needs some kind of flag to asks for a particular validation type, depth or scope. so that only that method is called
    this.validated = true;
    // added to prevent validate stages when using on /yourboxes validations
    if (scope === "packages") {
      this.validatePackageSelection(false);
    } else {
      this.validatePackageSelection();
      this.validateStages();
      this.validateAddressDetails();
      this.validateCarrierRestrictedAddresses();
      this.validateGoogleSelection();
      this.validateIsInternationalAndEUEnabled();
      // if no errors
      if (this.validationErrors?.length === 0) {
        // set boxes, trips and others
        this.validated = false;
        if (this.component) {
          this.component?.setShipMode(this.component.currentShipMode); // move this to component, after validation returns true
          this.component?.setTrips(this.component.trips); // move this to component, after validation returns true
          this.component?.setBoxes(this.component.boxes); // move this to component, after validation returns true
          this.component?.setUnits(this.component.currentUnit); // move this to component, after validation returns true
          this.component?.setPurpose(this.component.purposeSelected); // move this to component, after validation returns true
          // BIK-1783: re-run box validation after setBoxes to restore hasOverMaxPackage flags
          // setBoxes overwrites store boxes from the component's local copy which doesn't have the flags
          this.validateBoxes();
        }
      } else {
        this.validationMade = true;
      }
    }
    return !this.validationErrors?.length;
  }

  public resetValidations() {
    this.validated = false;
    this.validationMade = false;
    this.validationErrors = [];
    this.component?.clearActiveNotices();
  }

  public requestAlert(data: any) {
    this.component?.requestAlert(this.getFilteredAlertKey(data, "asObject"));
  }

  public addNotice(data: any) {
    this.component?.addNotice(this.getFilteredAlertKey(data));
  }

  public removeNotice(data: any) {
    this.component?.removeNotice(this.getFilteredAlertKey(data));
  }

  protected getFilteredAlertKey(
    data: any,
    returnType: string = "asString"
  ): any {
    const keyGetter = new AlertRequestQuery();

    if (data.hasOwnProperty("type")) {
      switch (data.type) {
        case "country":
          if(data.name === "intShip"){
            keyGetter.name = this.alertTypes[data.type].selector[data.name];
            keyGetter.typeSelector = data.name;
            break;
          }
          keyGetter.name = this.alertTypes[data.type].selector[data.event.direction];
          keyGetter.typeSelector = data.event.direction;
          keyGetter.replacements = {[this.alertTypes[data.type].needle[0]]: data.event.country,};
          break;
        case "rates":
          const countries = this.component?.currentCountries;
          let isCA = false;
          let name = "";
          for (const property in countries) {
            if (countries[property] == "CA") {
              isCA = true;
            }
          }
          const event = data.event;
          if (event && Object.keys(event).length > 0) {
            if (event.hasOwnProperty("results")) {
              if (event.results === false) {
                // timeout
                name = "timeOut";
              } else {
                name = "noResults";
              }
            } else if (isCA) {
                name = "canada";
              } else {
                name = "default";
              }
          }
          if (name) {
            keyGetter.name = this.alertTypes[data.type].selector[name];
            keyGetter.typeSelector = data.name;
            break;
          } else {
            return;
          }
        case "near":
        case "over":
        case "value":
        case "content":
        case "protection":
        case "purpose":
        case "stages":
        case "address": // BIK-2135
          keyGetter.name = this.alertTypes[data.type].selector[data.name];
          keyGetter.typeSelector = data.name;
          break;
      }
    }
    const filteredData = keyGetter.getKey(
      this.alertNames,
      this.alertTypes[data.type]
    );
    if (returnType === "asString") {
      return filteredData.name;
    } else {
      return filteredData;
    }
  }

  // this methods here should be private after Validate is given some kind of flag to asks for a particular validation type, depth or scope.
  // mostly an issue for the search module since its validation is non-standard
  public validatePackageSelection(validatePurpose = true) {
    // triggers on validate, and activates error highlights
    // clear prev errors
    try {
      this.resetValidations();
      const currentUnit = this.component?.currentUnit;
      const boxes = this.component?.boxes;

      const maxL =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxL
          : this.dimensionLimits.in.maxL;
      const maxW =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxW
          : this.dimensionLimits.in.maxW;
      const maxH =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxH
          : this.dimensionLimits.in.maxH;
      const maxWeight =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxWeight
          : this.dimensionLimits.in.maxWeight;
      const maxDimensional =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxDimensional
          : this.dimensionLimits.in.maxDimensional;
      const nearLimitDelta =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.nearLimitDelta
          : this.dimensionLimits.in.nearLimitDelta;
      let greaterProtection = 0;
      let lesserProtection = 0;

      boxes?.forEach((box: Box, index: number) => {
        this.component?.setBoxProperty({
          name: "hasOverMaxPackage",
          value: false,
          index,
        });
        this.component?.cleanBoxErrors(index);
        // This box Size Values
        const thisLength = Math.ceil(box.length ? box.length : 0);
        const thisWidth = Math.ceil(box.width ? box.width : 0);
        const thisHeight = Math.ceil(box.height ? box.height : 0);
        const thisWeight = Math.ceil(box.weight ? box.weight : 0);
        const thisMarketValue = Math.ceil(
          box.marketValue ? box.marketValue : 0
        );
        const thisContent = box.contents;
        const thisProtection = box.premiumProtection;
        const dimensions = new Array();
        dimensions.push(thisLength);
        dimensions.push(thisWidth);
        dimensions.push(thisHeight);
        dimensions.sort(function (a, b) {
          return b - a;
        });
        const dimensionalWeight =
          dimensions[0] + dimensions[1] * 2 + dimensions[2] * 2;

        // Validate E-bike notice
        if (!thisContent || !thisContent.label) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({
              index,
              error: "categorySelectionInvalid",
            });
          this.validationErrors.push("categorySelectionInvalid");
        } else if (
          thisContent &&
          thisContent.label &&
          thisContent.label.includes("E-Bike")
        ) {
          const countries = this.component?.currentCountries;
          let nonUS = false;
          for (const property in countries) {
            if (countries[property] != "US") {
              nonUS = true;
              break;
            }
          }
          if (!nonUS) {
            const trips = this.component?.trips;
            if (trips)
              for (let step = 0; step < trips.length; step++) {
                const from = trips[step].from;
                if (from && from.address_components) {
                  for (let i = 0; i < from.address_components.length; i++) {
                    const component = from.address_components[i];
                    if (
                      component &&
                      component.types[0] &&
                      component.types[0] === "administrative_area_level_1"
                    ) {
                      const state = component.short_name
                        ? component.short_name
                        : "";
                      if (state === "AK" || state === "HI") nonUS = true;
                    }
                  }
                }

                const to = trips[step].to;
                if (to && to.address_components) {
                  for (let i = 0; i < to.address_components.length; i++) {
                    const component = to.address_components[i];
                    if (
                      component &&
                      component.types[0] &&
                      component.types[0] === "administrative_area_level_1"
                    ) {
                      const state = component.short_name
                        ? component.short_name
                        : "";
                      if (state === "AK" || state === "HI") nonUS = true;
                    }
                  }
                }
              }
          }
          if (nonUS) {
            if (
              this.component &&
              typeof this.component.setBoxError === "function"
            )
              this.component.setBoxError({
                index,
                error: "categorySelectionInvalid",
              });
            this.validationErrors.push("EBikeInternational");
            this.requestAlert({ name: "eBike", type: "content" });
          } else {
            this.requestAlert({ name: "USeBike", type: "content" });
          }
        }

        // Validate Market Value
        if (thisMarketValue < this.minMarketValue) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "marketValueInvalid" });
          this.validationErrors.push("marketValueInvalid");
          // this.requestAlert({name:'zero', type:'value'});
        }

        // Validate Protection
        if (!thisProtection || !thisProtection.coverage || thisProtection.coverage > this.maxProtectionValue) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({
              index,
              error: "premiumProtectionInvalid",
            });
          this.validationErrors.push("premiumProtectionInvalid");
          // Only show the max protection alert when a value was actually selected
          if (thisProtection?.coverage > this.maxProtectionValue) {
            // BIK-1995: top banner removed — now shown inline via #after-protection slot
            // this.requestAlert({ name: "max", type: "protection" });
          }
        } else if (thisProtection.coverage < thisMarketValue) {
          // if package premium had been selected and is lower than market value
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({
              index,
              error: "premiumProtectionInvalid",
            });
          // BIK-1995: top banner removed — now shown inline via #after-protection slot
          // this.requestAlert({ name: "less", type: "protection" });
        } else {
          greaterProtection =
            greaterProtection < thisProtection.coverage
              ? thisProtection.coverage
              : greaterProtection;
          lesserProtection =
            lesserProtection > 0 && lesserProtection < thisProtection.coverage
              ? lesserProtection
              : thisProtection.coverage;
          if (lesserProtection < 1000 && greaterProtection > 1000) {
            // if market value is way too high
            // BIK-1995: top banner removed — now shown inline via #after-protection slot
            // this.requestAlert({ name: "high", type: "protection" });
          }
        }

        // Validate for empty or 0
        if (!thisLength) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "lengthInvalid" });
          this.validationErrors.push("lengthInvalid");
        }
        if (!thisWidth) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "widthInvalid" });
          this.validationErrors.push("widthInvalid");
        }
        if (!thisHeight) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "heightInvalid" });
          this.validationErrors.push("heightInvalid");
        }
        if (!thisWeight) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "weightInvalid" });
          this.validationErrors.push("weightInvalid");
        }

        // validate for Length
        if (thisLength > maxL) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "lengthInvalid" });
          this.validationErrors.push("lengthInvalid");
          // BIK-1995: top banner removed — now shown inline via GuidanceHelper
          // this.requestAlert({ name: "size", type: "over" });

        }
        // Validate Width
        else if (thisWidth > maxW) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "widthInvalid" });
          this.validationErrors.push("widthInvalid");
          // BIK-1995: top banner removed — now shown inline via GuidanceHelper
          // this.requestAlert({ name: "size", type: "over" });
          this.component?.setBoxProperty({
            name: "hasOverMaxPackage",
            value: true,
            index,
          });
        }
        // Validate Height
        else if (thisHeight > maxH) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "heightInvalid" });
          this.validationErrors.push("heightInvalid");
          // BIK-1995: top banner removed — now shown inline via GuidanceHelper
          // this.requestAlert({ name: "size", type: "over" });
          this.component?.setBoxProperty({
            name: "hasOverMaxPackage",
            value: true,
            index,
          });
        }
        // Validate Weight
        else if (thisWeight > maxWeight) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
            this.component.setBoxError({ index, error: "weightInvalid" });
          this.validationErrors.push("weightInvalid");
          // BIK-1995: top banner removed — now shown inline via GuidanceHelper
          // this.requestAlert({ name: "weight", type: "over" });
        }
        // check dimensions
        else if (dimensionalWeight > maxDimensional) {
          if (
            this.component &&
            typeof this.component.setBoxError === "function"
          )
          this.component.setBoxError({ index, error: "dimensionalInvalid" });
          this.validationErrors.push("dimensionalInvalid");
          // BIK-1995: top banner removed — now shown inline via GuidanceHelper
          // this.requestAlert({ name: "dim", type: "over" });
          this.removeNotice({ name: "dim", type: "near" });
        } else if (
          dimensionalWeight >= maxDimensional - nearLimitDelta &&
          dimensionalWeight <= maxDimensional
        ) {
          // BIK-1995: top banner removed — now shown inline via GuidanceHelper
          // this.requestAlert({ name: "dim", type: "near" });
          // BIK-505 hasOverMaxPackage warning within each package
          this.component?.setBoxProperty({
            name: "hasOverMaxPackage",
            value: true,
            index,
          });
        }
        // check Near Limits
        if (
          ((thisLength >= maxL - nearLimitDelta &&
            dimensionalWeight < maxDimensional) ||
            (thisWidth >= maxW - nearLimitDelta &&
              dimensionalWeight < maxDimensional) ||
            thisHeight >= maxH - nearLimitDelta) &&
          thisLength <= maxL &&
          thisWidth <= maxW &&
          thisHeight <= maxH &&
          dimensionalWeight < maxDimensional
        ) {
          // BIK-1995: top banner removed — now shown inline via GuidanceHelper
          // this.requestAlert({ name: "size", type: "near" });
          // BIK-505 hasOverMaxPackage warning within each package
          this.component?.setBoxProperty({
            name: "hasOverMaxPackage",
            value: true,
            index,
          });
        }

        // BIK-1904: Cubic volume validation
        this.checkCubicVolumeAlert(thisLength, thisWidth, thisHeight, thisWeight, currentUnit?.length || "in");
      });


      if (validatePurpose) {
        if (
          this.component?.purposeSelected === null ||
          this.component?.purposeSelected === false
        ) {
          this.validationErrors.push("purposeSelectionInvalid");
          this.component.purposeSelected = false;
        }
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  public validateBoxes() {
    // triggers everytime a box changes/is updated
    // clear prev errors
    try {
      this.resetValidations();
      const currentUnit = this.component?.currentUnit;
      const boxes = this.component?.boxes;
      const maxL =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxL
          : this.dimensionLimits.in.maxL;
      const maxW =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxW
          : this.dimensionLimits.in.maxW;
      const maxH =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxH
          : this.dimensionLimits.in.maxH;
      const maxWeight =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxWeight
          : this.dimensionLimits.in.maxWeight;
      const maxDimensional =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.maxDimensional
          : this.dimensionLimits.in.maxDimensional;
      const nearLimitDelta =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.nearLimitDelta
          : this.dimensionLimits.in.nearLimitDelta;
      const weightVerify =
        currentUnit?.length === "cm"
          ? this.dimensionLimits.cm.weightVerify
          : this.dimensionLimits.in.weightVerify;
      let greaterProtection = 0;
      let lesserProtection = 0;
      boxes?.forEach((box: Box, index: number) => {
        this.component?.setBoxProperty({
          name: "hasOverMaxPackage",
          value: false,
          index,
        });
        this.component?.cleanBoxErrors(index);
        // This box Size Values
        const boxLength = Math.ceil(box.length ? box.length : 0);
        const boxWidth = Math.ceil(box.width ? box.width : 0);
        const boxHeight = Math.ceil(box.height ? box.height : 0);
        const boxWeight = Math.ceil(box.weight ? box.weight : 0);
        const boxMarketValue = Math.ceil(box.marketValue ? box.marketValue : 0);
        const boxContent = box.contents;
        const boxProtection = box.premiumProtection;

        // Validate for E-bike content
        if (
          boxContent &&
          boxContent.label &&
          boxContent.label.includes("E-Bike")
        ) {
          const countries = this.component?.currentCountries;
          let nonUS = false;
          for (const property in countries) {
            if (countries[property] !== "US") {
              nonUS = true;
              break;
            }
          }
          if (!nonUS) {
            const trips = this.component?.trips;
            if (trips)
              for (let step = 0; step < trips.length; step++) {
                const from = trips[step].from;
                if (from && from.address_components) {
                  for (let i = 0; i < from.address_components.length; i++) {
                    const component = from.address_components[i];
                    if (
                      component &&
                      component.types[0] &&
                      component.types[0] === "administrative_area_level_1"
                    ) {
                      const state = component.short_name
                        ? component.short_name
                        : "";
                      if (state === "AK" || state === "HI") nonUS = true;
                    }
                  }
                }

                const to = trips[step].to;
                if (to && to.address_components) {
                  for (let i = 0; i < to.address_components.length; i++) {
                    const component = to.address_components[i];
                    if (
                      component &&
                      component.types[0] &&
                      component.types[0] === "administrative_area_level_1"
                    ) {
                      const state = component.short_name
                        ? component.short_name
                        : "";
                      if (state === "AK" || state === "HI") nonUS = true;
                    }
                  }
                }
              }
          }
          if (nonUS) {
            if (
              this.component &&
              typeof this.component.setBoxError === "function"
            )
              this.component.setBoxError({
                index,
                error: "categorySelectionInvalid",
              });
            this.validationErrors.push("EBikeInternational");
            this.requestAlert({ name: "eBike", type: "content" });
          } else {
            this.requestAlert({ name: "USeBike", type: "content" });
          }
        }

        // BIK-1995: top banner removed — Scicon is now shown inline as a
        // per-box danger alert via getActiveAlerts in components/ship/steps/Search.vue
        // if (box.preset && box.preset.toLowerCase().includes("scicon")) {
        //   this.requestAlert({ name: "scicon", type: "content" });
        // }

        // Validate Market Value
        if (box.marketValue && boxMarketValue < this.minMarketValue) {
          this.requestAlert({ name: "zero", type: "value" });
        }

        // Validate Protection
        if (boxProtection && boxProtection.coverage > this.maxProtectionValue) {
          // BIK-1995: top banner removed — now shown inline via #after-protection slot
          // this.requestAlert({ name: "max", type: "protection" });
        }
        if (boxProtection && boxProtection.coverage < boxMarketValue) {
          // if package premium had been selected and is lower than market value
          // BIK-1995: top banner removed — now shown inline via #after-protection slot
          // this.requestAlert({ name: "less", type: "protection" });
        }

        if (boxProtection) {
          greaterProtection =
            greaterProtection < boxProtection.coverage
              ? boxProtection.coverage
              : greaterProtection;
          lesserProtection =
            lesserProtection > 0 && lesserProtection < boxProtection.coverage
              ? lesserProtection
              : boxProtection.coverage;
          if (lesserProtection < 1000 && greaterProtection > 1000) {
            // if market value is way too high
            // BIK-1995: top banner removed — now shown inline via #after-protection slot
            // this.requestAlert({ name: "high", type: "protection" });
          }
        }

        // Validate Length
        if (boxLength > maxL) {
          // BIK-1995: top banner removed — now shown inline via GuidanceHelper
          // this.requestAlert({ name: "size", type: "over" });
          this.component?.setBoxProperty({
            name: "hasOverMaxPackage",
            value: true,
            index,
          });
        }
        // Validate Width
        else if (boxWidth > maxW) {
          // BIK-1995: top banner removed — now shown inline via GuidanceHelper
          // this.requestAlert({ name: "size", type: "over" });
          this.component?.setBoxProperty({
            name: "hasOverMaxPackage",
            value: true,
            index,
          });
        }
        // Validate Height
        else if (boxHeight > maxH) {
          // BIK-1995: top banner removed — now shown inline via GuidanceHelper
          // this.requestAlert({ name: "size", type: "over" });
          this.component?.setBoxProperty({
            name: "hasOverMaxPackage",
            value: true,
            index,
          });
        }
        // Validate Weight
        else if (boxWeight > maxWeight) {
          // BIK-1995: top banner removed — now shown inline via GuidanceHelper
          // this.requestAlert({ name: "weight", type: "over" });
          this.component?.setBoxProperty({
            name: "hasOverMaxPackage",
            value: true,
            index,
          });
        } else {
          // check near limits
          const L = box.length ? Math.ceil(box.length) : 0;
          const W = box.width ? Math.ceil(box.width) : 0;
          const H = box.height ? Math.ceil(box.height) : 0;
          const dimensions = new Array();
          dimensions.push(L);
          dimensions.push(W);
          dimensions.push(H);
          dimensions.sort(function (a, b) {
            return b - a;
          });
          const dimensionalWeight =
            dimensions[0] + dimensions[1] * 2 + dimensions[2] * 2;

          // check dimensions
          if (dimensionalWeight > maxDimensional) {
            this.validationErrors.push("dimensionalInvalid");
            // BIK-1995: top banner removed — now shown inline via GuidanceHelper
            // this.requestAlert({ name: "dim", type: "over" });
            this.removeNotice({ name: "dim", type: "near" });
          } else if (
            dimensionalWeight >= maxDimensional - nearLimitDelta &&
            dimensionalWeight <= maxDimensional
          ) {
            // BIK-1995: top banner removed — now shown inline via GuidanceHelper
            // this.requestAlert({ name: "dim", type: "near" });
            // BIK-505 hasOverMaxPackage warning within each package
            this.component?.setBoxProperty({
              name: "hasOverMaxPackage",
              value: true,
              index,
            });
          }

          // check Near Limits
          if (
            ((L >= maxL - nearLimitDelta &&
              dimensionalWeight < maxDimensional) ||
              (W >= maxW - nearLimitDelta &&
                dimensionalWeight < maxDimensional) ||
              H >= maxH - nearLimitDelta) &&
            L <= maxL &&
            W <= maxW &&
            H <= maxH &&
            dimensionalWeight < maxDimensional
          ) {
            // BIK-1995: top banner removed — now shown inline via GuidanceHelper
            // this.requestAlert({ name: "size", type: "near" });
            // BIK-505 hasOverMaxPackage warning within each package
            this.component?.setBoxProperty({
              name: "hasOverMaxPackage",
              value: true,
              index,
            });
          }

          let thisWeight = 0;
          if (typeof box.weight === "number") {
            thisWeight = Math.ceil(box.weight);
          }
          // BIK-1995: Bill's updated matrix scopes photo verification to 145–150 lb only
          // (the OVR near-max + at-max bands). 70–144 lb is the unified Heavy band with no photos.
          if (thisWeight >= weightVerify && thisWeight <= maxWeight) {
            // BIK-505 hasOverMaxPackage warning within each package
            this.component?.setBoxProperty({
              name: "hasOverMaxPackage",
              value: true,
              index,
            });
          }

          // BIK-1904: Cubic volume validation
          this.checkCubicVolumeAlert(L, W, H, thisWeight, currentUnit?.length || "in");
        }
      });
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  public validateStages() {
    if (this.component?.trips.length >= this.tripsLimit) {
      this.requestAlert({ name: "maxStage", type: "stages" });
    } else {
      this.removeNotice({ name: "maxStage", type: "stages" });
    }
    const countries = this.component?.currentCountries;
    const allowedCountries = this.component?.allowedCountries;
    for (let i = 0; i < countries?.length; i++) {
      const country = countries[i];
      const isCountryAllowed = allowedCountries.filter(
        (c: Country) => c.code === country?.short_name && c.active
      );
      if (!isCountryAllowed || !isCountryAllowed.length) {
        this.validationErrors.push("invalidCountry");
      }
    }
    // BIK-2084: same from/to location is no longer a blocking validation error.
    // It now surfaces as a non-blocking orange warning (AlertSameLocation),
    // handled in StageForm.checkIfEqual, so users can proceed when they really
    // do want to ship to the same place.
  }

  public validateIsInternationalAndEUEnabled(){
    // check if InternationalShipmentIsEnabled
    // 0 Disabled; 1 Enabled; 2 Intra CA. and only from/to US
      // BIK-2140: intShipStatus loads async — Search.mounted() fires
      // getInternationalShippingStatus() without awaiting it — so this can run before the value
      // exists. Until it lands it is undefined, which matched neither "0"/"1"/"2" and fell
      // through to `default:`, showing the "we are temporarily restricting our shipping
      // service" banner purely because the status had not loaded yet.
      // A missing value means "still loading", not "disabled": skip validation and let the
      // re-run decide once the real value is in. `default:` stays for genuinely unknown codes.
      if (this.component?.intShipStatus?.value === undefined) return;
      // BIK-2140: work out whether the restriction applies, then apply OR clear it. The old
      // code only ever pushed — `case "1"` did nothing at all — so once the alert had been
      // raised (by the undefined-value fall-through above, or by an earlier trip state) it
      // stayed on screen forever: requestAlert() adds the key to activeNotices and nothing
      // removed it again. Mirrors how validateAddressDetails() clears its own errors.
      let isRestricted = false;
      switch (this.component?.intShipStatus.value){
        case "0":
          isRestricted = true;
          break;
        case "1":
          // Enabled — no restriction.
          break;
        case "2":
          this.component?.trips.forEach((trip) => {

            if( trip.from?.address_components && trip.to?.address_components ){
              const fromComponents = StoreHelper.mapAddressComponents(
                trip.from.address_components,
                trip.from.formatted_address
              );
              const toComponents = StoreHelper.mapAddressComponents(
                trip.to.address_components,
                trip.to.formatted_address
              );

              const fromCountryCode = fromComponents?.countryCode;
              const toCountryCode = toComponents?.countryCode;

              if( fromCountryCode && toCountryCode ){
                const isIntraCanada = fromCountryCode == 'CA' && toCountryCode == 'CA';
                const involvesUS = fromCountryCode == 'US' || toCountryCode == 'US';

                if ( !isIntraCanada && !involvesUS ){
                  isRestricted = true;
                }
              }
            }
          });
          break;
        default:
          // A code we do not know about — stay conservative and restrict.
          isRestricted = true;
          break;
      }

      if (isRestricted) {
        this.validationErrors.push("No3P3CAtThisTime");
        this.requestAlert({ name: "intShip", type: "country" });
      } else {
        this.validationErrors = this.validationErrors.filter(
          (error) => error !== "No3P3CAtThisTime"
        );
        this.removeNotice({ name: "intShip", type: "country" });
      }
  }

  public validateGoogleSelection() {
    if (this.component?.details) {
      for (let step = 0; step < this.component?.details.length; step++) {
        const to = this.component.details[step].to;
        const from = this.component.details[step].from;
        if (
          !to.passedAVS &&
          to.avsChecked &&
          to.searchType?.toLowerCase() === "full"
        ) {
          // BIK-1995: top banner removed — AVS failure now shown inline via avsMessage("apiFailed")
          // this.component?.addNotice("warningAVSFailed");
        }
        if (
          !from.passedAVS &&
          from.avsChecked &&
          from.searchType?.toLowerCase() === "full"
        ) {
          // BIK-1995: top banner removed — AVS failure now shown inline via avsMessage("apiFailed")
          // this.component?.addNotice("warningAVSFailed");
        }
      }
    }
  }

  /**
   * BIK-2135: hard-stop Ship1 on destinations UPS will not deliver to —
   * FedEx offices/drop boxes, USPS post offices and PO/APO/FPO boxes.
   *
   * Previously these were only detected on Ship3 (ValidationDetails), and only
   * as a warning for FedEx/USPS, so Ship1 let the search through with no alert
   * at all and bikes got shipped to undeliverable addresses. Bill: "we want to
   * not let folks enter FEDEX and USPS as we had it previously".
   *
   * Gated on avsRawPasteDirect so flag-OFF keeps the legacy behavior exactly.
   */
  public validateCarrierRestrictedAddresses() {
    if (!this.component?.avsRawPasteDirect) {
      return;
    }
    const details = this.component?.details;
    if (!details) {
      return;
    }

    for (let step = 0; step < details.length; step++) {
      const from = details[step]?.from;
      const to = details[step]?.to;

      // Order matters: PO Box is checked first so a "Post Office Box 9" street
      // line shows the PO Box copy rather than the post-office copy.
      if (isPoBox(to)) {
        this.validationErrors.push("carrierRestrictedTo" + step);
        this.requestAlert({ name: "poBox", type: "address" });
      } else if (isFedExOffice(to)) {
        this.validationErrors.push("carrierRestrictedTo" + step);
        this.requestAlert({ name: "fedEx", type: "address" });
      } else if (isPostalService(to)) {
        // No dedicated USPS-destination alert exists; POAddressWarning's copy
        // ("does not offer shipping to a P.O.Box...") is the accurate fit.
        this.validationErrors.push("carrierRestrictedTo" + step);
        this.requestAlert({ name: "poBox", type: "address" });
      } else if (isUpsStore(to)) {
        // Warning only — a UPS Store is a valid UPS destination.
        this.requestAlert({ name: "ups", type: "address" });
      }

      if (isPoBox(from)) {
        this.validationErrors.push("carrierRestrictedFrom" + step);
        this.requestAlert({ name: "poFromBox", type: "address" });
      } else if (isFedExOffice(from)) {
        this.validationErrors.push("carrierRestrictedFrom" + step);
        this.requestAlert({ name: "fedEx", type: "address" });
      } else if (isPostalService(from)) {
        this.validationErrors.push("carrierRestrictedFrom" + step);
        this.requestAlert({ name: "poFromBox", type: "address" });
      } else if (isUpsStore(from)) {
        // Warning only — affix the label yourself and get a drop-off receipt.
        this.requestAlert({ name: "upsFrom", type: "address" });
      }
    }
  }

  public validateAddressDetails() {
    if (this.component?.details) {
      if (this.component.details.length === 0) {
        this.validationErrors.push("missingAddressFrom0");
        this.validationErrors.push("missingAddressTo0");
        return;
      }
      for (let step = 0; step < this.component.details.length; step++) {
        if (this.component.details[step]?.from) {
          if (!IsAddressFilled(this.component.details[step].from)) {
            this.validationErrors.push("missingFrom" + step);
          } else if (this.validationErrors.includes("missingFrom" + step)) {
            this.validationErrors = this.validationErrors.filter(
              (error) => error !== "missingFrom" + step
            );
          }
        } else {
          this.validationErrors.push("missingAddressFrom" + step);
        }
        if (this.component.details[step].to) {
          if (!IsAddressFilled(this.component.details[step].to)) {
            this.validationErrors.push("missingTo" + step);
          } else if (this.validationErrors.includes("missingTo" + step)) {
            this.validationErrors = this.validationErrors.filter(
              (error) => error !== "missingTo" + step
            );
          }
        } else {
          this.validationErrors.push("missingAddressTo" + step);
        }
      }
    }
  }
}
